import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroVideo from "@/assets/animated_bullgrape.mp4";
import heroVideoMobile from "@/assets/animated_bullgrape_mobile.mp4";
import heroPosterDesktop1280 from "@/assets/hero-poster-desktop-1280.webp";
import heroPosterDesktop1920 from "@/assets/hero-poster-desktop-1920.webp";
import heroPosterMobile480 from "@/assets/hero-poster-mobile-480.webp";
import heroPosterMobile768 from "@/assets/hero-poster-mobile-768.webp";
import { useIsMobile } from "@/hooks/use-mobile";

const Hero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMouseXRef = useRef<number | null>(null);
  const targetTimeRef = useRef<number>(5.0);
  const isSeekingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef(false);
  const isMobile = useIsMobile();
  const videoSrc = isMobile ? heroVideoMobile : heroVideo;
  const posterSrc = isMobile ? heroPosterMobile480 : heroPosterDesktop1280;
  const [videoLoad, setVideoLoad] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const { t } = useTranslation();
  const bigTitle = t("hero.bigTitle", { returnObjects: true }) as string[];
  const capabilities = t("hero.capabilities", { returnObjects: true }) as string[];

  // Lazy-load the hero video only when the section is in view
  useEffect(() => {
    if (videoLoad) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVideoLoad(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVideoLoad(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [videoLoad]);

  useEffect(() => {
    if (!videoLoad) return;
    const video = videoRef.current;
    if (!video) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isCoarse =
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches;

    const MIN_TIME = 2.0;
    const START_TIME = 5.0;
    const MAX_TIME = 7.0;
    const SCRUB_SENSITIVITY = 0.8;
    const SEEK_EPSILON = 0.035;

    const clamp = (value: number, min: number, max: number) => {
      return Math.max(min, Math.min(max, value));
    };

    const requestSeek = () => {
      const video = videoRef.current;
      if (!video) return;
      if (!Number.isFinite(video.duration)) return;
      if (isSeekingRef.current) return;
      const nextTime = targetTimeRef.current;
      if (Math.abs(video.currentTime - nextTime) < SEEK_EPSILON) return;
      isSeekingRef.current = true;
      try {
        video.currentTime = nextTime;
      } catch {
        isSeekingRef.current = false;
      }
    };

    const setInitialFrame = () => {
      video.pause();
      targetTimeRef.current = START_TIME;
      isSeekingRef.current = false;
      try {
        video.currentTime = START_TIME;
      } catch {}
      setVideoReady(true);
    };

    const handleSeeked = () => {
      isSeekingRef.current = false;
      const video = videoRef.current;
      if (!video) return;
      if (Math.abs(video.currentTime - targetTimeRef.current) > SEEK_EPSILON) {
        requestSeek();
      }
    };

    const forcePause = () => {
      video.pause();
    };

    const scheduleSeek = () => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      rafRef.current = requestAnimationFrame(() => {
        pendingRef.current = false;
        requestSeek();
      });
    };

    const handleMouseMove = (event: MouseEvent) => {
      const v = videoRef.current;
      if (!v || !Number.isFinite(v.duration)) return;
      v.pause();
      if (lastMouseXRef.current === null) {
        lastMouseXRef.current = event.clientX;
        return;
      }
      const deltaX = event.clientX - lastMouseXRef.current;
      lastMouseXRef.current = event.clientX;
      const scrubRange = MAX_TIME - MIN_TIME;
      const deltaTime = (deltaX / window.innerWidth) * scrubRange * SCRUB_SENSITIVITY;
      targetTimeRef.current = clamp(
        targetTimeRef.current + deltaTime,
        MIN_TIME,
        MAX_TIME
      );
      scheduleSeek();
    };

    const handleMouseEnter = (event: MouseEvent) => {
      lastMouseXRef.current = event.clientX;
    };

    const handleMouseLeave = () => {
      lastMouseXRef.current = null;
    };

    video.addEventListener("loadedmetadata", setInitialFrame);
    video.addEventListener("loadeddata", setInitialFrame);
    video.addEventListener("canplay", () => setVideoReady(true));
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("play", forcePause);
    // Kick off metadata load now that the section is in view.
    try { video.load(); } catch {}

    // Only wire mouse scrubbing on fine-pointer (desktop) + motion-allowed users.
    const scrubEnabled = !reduceMotion && !isCoarse;
    if (scrubEnabled) {
      window.addEventListener("mousemove", handleMouseMove, { passive: true });
      window.addEventListener("mouseenter", handleMouseEnter);
      window.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      video.removeEventListener("loadedmetadata", setInitialFrame);
      video.removeEventListener("loadeddata", setInitialFrame);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("play", forcePause);
      if (scrubEnabled) {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseenter", handleMouseEnter);
        window.removeEventListener("mouseleave", handleMouseLeave);
      }
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [videoLoad]);

  return (
    <section aria-label="Hero" className="hero-root relative min-h-[100svh] md:min-h-screen w-full overflow-hidden grain">
      {/* Mouse-scrubbed video */}
      <div ref={containerRef} aria-hidden className="absolute inset-0 z-0">
        {videoLoad && (
          <video
            ref={videoRef}
            className={`hero-video w-full h-full object-cover object-[center_82%] md:object-center transition-opacity duration-300 ${videoReady ? "md:opacity-100 opacity-[0.66]" : "opacity-0"}`}
            key={videoSrc}
            src={videoSrc}
            poster={posterSrc}
            muted
            playsInline
            preload="metadata"
          />
        )}
        {/* Responsive poster overlay — removed once the video is ready */}
        {!videoReady && (
          <picture>
            <source
              media="(max-width: 767px)"
              type="image/webp"
              srcSet={`${heroPosterMobile480} 480w, ${heroPosterMobile768} 768w`}
              sizes="100vw"
            />
            <source
              media="(min-width: 768px)"
              type="image/webp"
              srcSet={`${heroPosterDesktop1280} 1280w, ${heroPosterDesktop1920} 1920w`}
              sizes="100vw"
            />
            <img
              src={heroPosterDesktop1280}
              alt=""
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
              className="hero-video absolute inset-0 w-full h-full object-cover object-[center_82%] md:object-center"
            />
          </picture>
        )}
        {/* Cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/20 to-background" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 45%, hsl(var(--accent) / 0.22) 0%, transparent 55%)",
          }}
        />
        <div className="absolute inset-0 dot-grid opacity-30" />
        {/* Mobile gradient: dark at top for text, lighter in middle for bull visibility, dark at bottom for CTAs */}
        <div
          className="absolute inset-0 md:hidden pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.28) 30%, rgba(0,0,0,0.50) 65%, rgba(0,0,0,0.82) 100%)",
          }}
        />
      </div>

      {/* Corner HUD */}
      <div className="hero-hud absolute top-[5.5rem] md:top-36 left-0 right-0 px-6 md:px-12 z-10 font-mono text-[10px] tracking-[0.25em] md:tracking-[0.3em] text-white/80 uppercase flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 pointer-events-none">
        <div className="flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          {t("hero.hudBrand")}
        </div>
        <div className="md:text-right hidden md:block">{t("hero.hudLocation")}</div>
      </div>

      {/* Headline row — top on mobile, bottom on desktop */}
      <div className="hero-content absolute top-[8rem] md:top-auto md:bottom-24 left-0 right-0 z-10 px-6 md:px-12 lg:px-16 pointer-events-none">
        <div className="hero-content-row flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-16">
          {/* Left / top */}
          <div className="hero-left md:max-w-2xl pointer-events-auto">
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-accent mb-2 md:mb-3">
              {t("hero.badge01")}
            </p>
            <h1 className="hero-title font-anton uppercase text-white neon-glow">
              {bigTitle.map((line, i) => (
                <span key={i} className="hero-title-line">
                  {line}
                </span>
              ))}
            </h1>
          </div>

          {/* Right / below */}
          <div className="hero-right max-w-xs md:max-w-md md:text-right pointer-events-auto">
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/80 mb-2 md:mb-3">
              {t("hero.badge02")}
            </p>
            <p className="hero-lead font-mono text-xs md:text-sm text-white/80 md:text-white/90 leading-[1.55] max-w-[32ch] md:max-w-md mb-4 md:mb-5">
              {t("hero.leadShort")}
            </p>
            <div className="hero-ctas flex flex-col md:flex-row md:flex-wrap md:justify-end gap-1 md:gap-3">
              <a
                href="#contact"
                className="hero-cta-primary rounded-full glass neon-ring inline-flex items-center justify-center gap-2 px-5 py-4 min-h-[56px] md:min-h-[52px] md:py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white hover:bg-accent/20 transition-colors w-full md:w-auto"
              >
                {t("hero.startProject")} <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <a
                href="#work"
                className="hero-cta-secondary inline-flex items-center justify-center gap-2 py-1.5 md:px-5 md:py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-white transition-colors md:rounded-full md:border md:border-white/20 md:text-white/90"
              >
                {t("hero.seeWork")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Capability marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 glass overflow-hidden hidden md:block">
        <div className="flex whitespace-nowrap animate-marquee py-3 font-mono text-xs tracking-[0.3em] uppercase">
          {[...capabilities, ...capabilities, ...capabilities, ...capabilities].map((c, i) => (
            <span key={i} className="mx-6 flex items-center gap-6 text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Skip link */}
      <a
        href="#work"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:bg-accent focus:text-accent-foreground focus:px-4 focus:py-2 focus:font-mono focus:text-xs focus:uppercase"
      >
        Skip hero
      </a>
    </section>
  );
};

export default Hero;
